import { formatPriceWithCurrency } from '@toggle/helpers';
import { AssetClassCode, formatTicker, periods } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { PriceMovementChart } from '~/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { EntityHeader } from '~/modules/entity-header/EntityHeader';
import {
  filterPredictions,
  formatUtcDate,
  getInsightCondition,
} from '~/services/insights/utils/insight-utils';
import { InsightWidgetData } from '~/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { getPredictionsTableData } from '~/views/turbo-chart/components/insights/utils/insights-utils/insights-utils';
import { AssessmentWidget } from '~/widgets/assessment-widget/AssessmentWidget';
import { InsightPastEpisodes } from '~/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';
import { PredictionSummaryTable } from '~/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';

import { InsightSummary } from '../../../insight-summary/InsightSummary';
import { InsightsReaction } from '../insights-reaction/InsightsReaction';
import { ViewAllInsightsSection } from '../view-all-insights-section/ViewAllInsightsSection';
import * as S from './InsightsDrawerContent.styles';

const IDX_MAX = 126;

export type InsightsDrawerContentProps = {
  insightsData: InsightWidgetData;
  header?: () => React.JSX.Element;
  showViewMore?: boolean;
  showEntityHeader?: boolean;
};

export const InsightsDrawerContent = ({
  insightsData,
  header,
  showViewMore = true,
  showEntityHeader = true,
}: InsightsDrawerContentProps) => {
  const { t } = useTranslation(['scenarioCommander', 'widget']);
  const { insight, predictions, entity, snakeTs } = insightsData;

  const createdAtDate = formatUtcDate(insight.created_at, 'PP');
  const lastValueDate = insight.metadata.last_value_date
    ? formatUtcDate(insight.metadata.last_value_date, 'PP')
    : undefined;
  const entityName = entity.name;
  const isFixedIncome =
    entity.asset_class === AssetClassCode.Fi && entity.class === 6;

  const episodeCount = insight.episodes.length;
  const condition = getInsightCondition(insight, t);

  const horizons = periods;
  const predictionByHorizon = useMemo(() => {
    return getPredictionsTableData(horizons, predictions);
  }, [predictions, horizons]);

  return (
    <S.Container>
      <S.InsightTopWrapper>
        {showEntityHeader && (
          <EntityHeader
            entity={entity}
            truncateLongTitle
            truncateAtLength={25}
            isDrawer
            headerButton={<></>}
          />
        )}
        {header && header()}
        <InsightSummary insight={insight} isFixedIncome={isFixedIncome} />
        {lastValueDate && (
          <S.ValueInfo>
            {t('widget:insights:analysisBased', {
              price: formatPriceWithCurrency(insight.metadata.last_value),
              createdAtDate: createdAtDate,
              lastValueDate: lastValueDate,
              ticker: formatTicker(entity),
            })}
          </S.ValueInfo>
        )}
        <InsightsReaction
          entityName={entityName}
          episodeCount={episodeCount}
          isFixedIncome={isFixedIncome}
        />
        <PriceMovementChart
          prediction={filterPredictions(insightsData, IDX_MAX).predictions}
          snakeTs={snakeTs}
          horizonToIndexOffset={1}
          bestHorizon={insight.metadata.horizon}
          isFixedIncome={isFixedIncome}
          useDynamicDaysLabel
        />
        <PredictionSummaryTable
          data={predictionByHorizon}
          isMedian
          horizons={horizons}
          isFixedIncome={isFixedIncome}
        />
      </S.InsightTopWrapper>
      <S.DeepDiveWrapper>
        <S.StyledTitle>{t('scenarioCommander:deepDive')}</S.StyledTitle>
        <ConditionSection condition={condition} />
        <InsightPastEpisodes
          insight={insight}
          entity={entity}
          variant={'insight-past-episodes'}
        />
      </S.DeepDiveWrapper>
      <div>
        <AssessmentWidget
          isFixedIncome={isFixedIncome}
          gaugeValue={insight.metadata.stars}
          horizon={insight.metadata.horizon}
          median={insight.metadata.median_return}
          checklist={insight.metadata.high_conviction_report}
          hasHeader={false}
          titleElement={
            <S.StyledTitle>
              {t('scenarioCommander:scenarioQuality')}
            </S.StyledTitle>
          }
        />
      </div>
      {showViewMore && (
        <ViewAllInsightsSection
          entityName={entityName}
          entityTag={entity.tag}
          insightId={insight.id}
        />
      )}
    </S.Container>
  );
};

InsightsDrawerContent.Styled = S;
